import React from 'react';
import { MessageCircle, GraduationCap, ClipboardCheck, Mic2, Layers, Languages } from 'lucide-react';

export type SessionMode =
  | 'a1_sprechen'
  | 'free_conversation'
  | 'exam_prep'
  | 'mock_test'
  | 'pronunciation'
  | 'topic_deep_dive';

interface ModeSelectorProps {
  selected: SessionMode;
  onChange: (mode: SessionMode) => void;
}

const MODES: { id: SessionMode; label: string; description: string; Icon: React.FC<any> }[] = [
  {
    id: 'a1_sprechen',
    label: 'A1 Sprechen',
    description: 'Goethe A1 speaking exam — Teil 1, 2 & 3',
    Icon: Languages,
  },
  {
    id: 'free_conversation',
    label: 'Free Conversation',
    description: 'Open, natural dialogue',
    Icon: MessageCircle,
  },
  {
    id: 'exam_prep',
    label: 'Exam Prep',
    description: 'CEFR-aligned practice',
    Icon: GraduationCap,
  },
  {
    id: 'mock_test',
    label: 'Mock Test',
    description: 'Timed exam simulation',
    Icon: ClipboardCheck,
  },
  {
    id: 'pronunciation',
    label: 'Pronunciation',
    description: 'Sound and accent focus',
    Icon: Mic2,
  },
  {
    id: 'topic_deep_dive',
    label: 'Topic Deep Dive',
    description: 'Go deep on one subject',
    Icon: Layers,
  },
];

const ModeSelector: React.FC<ModeSelectorProps> = ({ selected, onChange }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {MODES.map(({ id, label, description, Icon }) => {
        const isActive = selected === id;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.875rem 1rem',
              borderRadius: 'var(--radius-lg)',
              background: isActive ? 'var(--accent-indigo-dim)' : 'var(--bg-glass)',
              border: `1px solid ${isActive ? 'var(--accent-indigo)' : 'var(--border-glass)'}`,
              color: isActive ? '#a5b4fc' : 'var(--text-secondary)',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all var(--transition-fast)',
              backdropFilter: 'blur(12px)',
              boxShadow: isActive ? '0 0 20px rgba(99,102,241,0.15)' : 'none',
            }}
            onMouseEnter={e => {
              if (!isActive) {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-glass-hover)';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)';
              }
            }}
            onMouseLeave={e => {
              if (!isActive) {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-glass)';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)';
              }
            }}
          >
            <Icon size={16} style={{ flexShrink: 0, opacity: isActive ? 1 : 0.6 }} />
            <div>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, margin: 0, lineHeight: 1.2 }}>
                {label}
              </p>
              <p style={{ fontSize: '0.725rem', color: isActive ? 'rgba(165,180,252,0.7)' : 'var(--text-tertiary)', margin: 0, marginTop: '0.15rem' }}>
                {description}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ModeSelector;
